import type { CSSProperties } from "react";

/** Clamp UI playhead percent (0–100) from timeline/autoplay/scrub sources. */
export function clampMediaPlayerPlayheadPercent(
	playheadPercent: number,
): number {
	if (!Number.isFinite(playheadPercent)) {
		return 0;
	}
	return Math.max(0, Math.min(100, playheadPercent));
}

/** Left edge softening (percent of track width). */
const TRACK_GLOW_START_FADE_PERCENT = 6;

/** Playhead-side fade width (percent of track); moves with the dragger. */
const TRACK_GLOW_PLAYHEAD_EDGE_FADE_PERCENT = 10;

export interface MediaPlayerTrackGlowMaskOptions {
	thumbDiameterPx?: number;
	trackWidthPx?: number;
}

/**
 * Horizontal centre of the scrub thumb on the track (0–100), matching
 * `clamp(0px, calc(playhead% - halfThumb), calc(100% - thumb))` layout.
 */
export function resolveMediaPlayerThumbCenterPercent(
	playheadPercent: number,
	thumbDiameterPx: number,
	trackWidthPx: number,
): number {
	const playhead = clampMediaPlayerPlayheadPercent(playheadPercent);
	if (trackWidthPx <= 0 || thumbDiameterPx <= 0) {
		return playhead;
	}

	const thumbHalfPx = thumbDiameterPx / 2;
	const playheadPx = (playhead / 100) * trackWidthPx;
	const leftPx = Math.max(
		0,
		Math.min(trackWidthPx - thumbDiameterPx, playheadPx - thumbHalfPx),
	);

	return clampMediaPlayerPlayheadPercent(
		((leftPx + thumbHalfPx) / trackWidthPx) * 100,
	);
}

function resolveMediaPlayerGlowMaskEndPercent(
	playheadPercent: number,
	options?: MediaPlayerTrackGlowMaskOptions,
): number {
	const playhead = clampMediaPlayerPlayheadPercent(playheadPercent);
	const thumbDiameterPx = options?.thumbDiameterPx;
	const trackWidthPx = options?.trackWidthPx;

	if (
		thumbDiameterPx != null &&
		thumbDiameterPx > 0 &&
		trackWidthPx != null &&
		trackWidthPx > 0
	) {
		return resolveMediaPlayerThumbCenterPercent(
			playhead,
			thumbDiameterPx,
			trackWidthPx,
		);
	}

	return playhead;
}

function buildHorizontalGlowMask(maskEndPercent: number): string {
	const end = clampMediaPlayerPlayheadPercent(maskEndPercent);
	if (end <= 0) {
		return "linear-gradient(to right, transparent 0%, transparent 100%)";
	}

	const edgeFadeStart = Math.max(
		0,
		end - TRACK_GLOW_PLAYHEAD_EDGE_FADE_PERCENT,
	);

	if (end <= TRACK_GLOW_START_FADE_PERCENT) {
		const solidStart = Math.max(0, end - Math.min(2, end * 0.35));
		return `linear-gradient(to right, transparent 0%, #000 ${solidStart}%, transparent ${end}%)`;
	}

	const solidThrough = Math.max(
		TRACK_GLOW_START_FADE_PERCENT,
		Math.min(edgeFadeStart, end - 0.5),
	);

	return `linear-gradient(to right, transparent 0%, #000 ${TRACK_GLOW_START_FADE_PERCENT}%, #000 ${solidThrough}%, transparent ${end}%)`;
}

/**
 * Full-width aurora + dynamic mask: reveal 0 → dragger centre with a soft fade at
 * the playhead edge (no scaleX — curves stay unstretched).
 */
export function buildMediaPlayerTrackGlowMaskStyle(
	playheadPercent: number,
	options?: MediaPlayerTrackGlowMaskOptions,
): CSSProperties | null {
	const maskEnd = resolveMediaPlayerGlowMaskEndPercent(
		playheadPercent,
		options,
	);
	if (maskEnd <= 0) {
		return null;
	}

	const horizontalMask = buildHorizontalGlowMask(maskEnd);
	const verticalMask =
		"linear-gradient(to bottom, transparent 0%, #000 14%, #000 86%, transparent 100%)";

	return {
		maskImage: `${horizontalMask}, ${verticalMask}`,
		WebkitMaskImage: `${horizontalMask}, ${verticalMask}`,
		maskComposite: "intersect",
		WebkitMaskComposite: "source-in",
		maskSize: "100% 100%",
		WebkitMaskSize: "100% 100%",
		maskRepeat: "no-repeat",
		WebkitMaskRepeat: "no-repeat",
	};
}
